import React, { useCallback, useMemo, useState } from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import { Delete } from '@material-ui/icons';
import { v4 as uuid } from 'uuid';

import { useLiveRef } from 'ui-utils';

import Button, { ButtonProps } from './Button';
import IconButton, { IconButtonProps } from './IconButton';
import TextField, { TextFieldProps } from './TextField';
import Typography from './Typography';

const useStyles = (theme: Theme) =>
  createStyles({
    container: {
      display: 'grid',
      gridTemplateColumns: '1fr auto 1fr auto',
      columnGap: theme.spacing.unit * 2,
      marginBottom: theme.spacing.unit * 2,
      alignItems: 'end',
    },
    to: {
      textAlign: 'center',
      marginBottom: theme.spacing.unit * 2,
    },
  });

type LinkReplacerState = Array<{ find: string; replace: string; key?: string }>;

type LinkReplacerStateUpdater = (value: LinkReplacerState) => void;

type UseLinkReplacer = {
  initialState?: LinkReplacerState;
  controller?: [LinkReplacerState, LinkReplacerStateUpdater] | [];
  onChange?: LinkReplacerStateUpdater;
};

interface LinkReplacerProps extends WithStyles<typeof useStyles> {
  value: LinkReplacerState | [];
  onChange: (event: React.SyntheticEvent, eventInfo: { index: number; type: 'find' | 'replace' }) => void;
  onRemove: (event: React.SyntheticEvent, eventInfo: { index: number }) => void;
  onAdd: () => void;
  textFieldProps?: TextFieldProps;
  iconButtonProps?: IconButtonProps;
  buttonProps?: ButtonProps;
  readOnly?: boolean;
}

const LinkReplacer: React.FC<LinkReplacerProps> = ({
  classes,
  value = [],
  onChange,
  onRemove,
  onAdd,
  textFieldProps,
  iconButtonProps,
  buttonProps,
  readOnly,
}) => (
  <div>
    {value.map(({ find, replace, key }, index) => (
      <div key={key || index} className={classes.container}>
        <TextField
          label="Find"
          required
          fullWidth
          rounded
          InputLabelProps={{
            shrink: true,
          }}
          value={find}
          onChange={(event) => onChange(event, { index, type: 'find' })}
          {...textFieldProps}
        />
        <Typography className={classes.to} variant="h3W500F15">
          to
        </Typography>
        <TextField
          label="Replace"
          required
          fullWidth
          rounded
          InputLabelProps={{
            shrink: true,
          }}
          value={replace}
          onChange={(event) => onChange(event, { index, type: 'replace' })}
          {...textFieldProps}
        />
        {!readOnly && (
          <IconButton onClick={(event) => onRemove(event, { index })} {...iconButtonProps}>
            <Delete />
          </IconButton>
        )}
      </div>
    ))}
    {!readOnly && (
      <Button onClick={onAdd} variant="outlined" rounded {...buttonProps}>
        <Typography>Add item</Typography>
      </Button>
    )}
  </div>
);

/**
 * Helper hook for the LinkReplacement component that takes care of the boilerplate that would come with implementing the add, remove and change text logic.
 * NOTE: This hook it not necessary to use the component at all. It's just a bundle of boilerplate bussiness logic to help implementation.
 * @param {UseLinkReplacer} configs - The configs to make the component controlled of not.
 * @param {UseLinkReplacer['initialState']} configs.initialState - The initial state for the component if you want to not control the state.
 * @param {UseLinkReplacer['controller']} configs.controller - A pair of state and state updater so that you can control the state from outside.
 * @param {UseLinkReplacer['onChange']} configs.onChange - A callback that will listen to any changes to the components state add, remove, text changes.
 */

export function useLinkReplacer({ initialState = [], controller = [], onChange = () => {} }: UseLinkReplacer = {}) {
  const [linkReplacer, setLinkReplacer] = useState(() => initialState.map((item) => ({ ...item, key: uuid() })));
  const [state, stateUpdater] = controller;

  const cbRef = useLiveRef(onChange);

  const stateToUse = (stateUpdater ? state : linkReplacer) as LinkReplacerState;
  const updater = stateUpdater || (setLinkReplacer as LinkReplacerStateUpdater);

  const handleChange = useCallback(
    (event, { index, type }: { index: number; type: 'find' | 'replace' }) => {
      const newUrlReplacer = [...stateToUse].map((item, linkIndex) => {
        if (index === linkIndex) return { ...item, [type]: event.target.value };
        return item;
      });
      cbRef.current(newUrlReplacer);
      updater(newUrlReplacer);
    },
    [updater, cbRef, stateToUse],
  );

  const handleRemove = useCallback(
    (_event, { index }: { index: number }) => {
      const newUrlReplacer = [...stateToUse].filter((_item, replacerIndex) => index !== replacerIndex);
      cbRef.current(newUrlReplacer);
      updater(newUrlReplacer);
    },
    [updater, cbRef, stateToUse],
  );

  const handleAdd = useCallback(() => {
    const newUrlReplacer = [...stateToUse, { find: '', replace: '', key: uuid() }];
    cbRef.current(newUrlReplacer);
    updater(newUrlReplacer);
  }, [updater, cbRef, stateToUse]);

  const props = useMemo(
    () => ({
      value: stateToUse,
      onChange: handleChange,
      onRemove: handleRemove,
      onAdd: handleAdd,
    }),
    [stateToUse, handleChange, handleRemove, handleAdd],
  );

  return { state: stateToUse, handleChange, handleRemove, handleAdd, props } as const;
}

export default withStyles(useStyles)(LinkReplacer);
